import {
  IBaseDataLayer,
  IDataLayerVariable,
  PageType,
} from "@almamedia/amr-adtech-stack/lib/services/dataLayer";

type EoLocation = {
  countryCode: "FI";
  countryName: string | null;
  regionCode: string | null;
  regionName: string | null;
  municipalityCode: string | null;
  municipalityName: string | null;
  districtCode: string | null;
  districtName: string | null;
  nickname: string | null;
  postCodeAndOffice: string | null;
  countries: string[];
  regions: string[];
  municipalities: string[];
  districts: string[];
  nicknames: string[];
  municipalityFromUrl: string | null;
  districtFromUrl: string | null;
};

interface ICustomVariables extends IBaseDataLayer {
  destinationURL: IDataLayerVariable<string>;
  referrer: IDataLayerVariable<string>;
  subCategory: IDataLayerVariable<string>;
  validate: IDataLayerVariable<boolean>;
  viewPort: IDataLayerVariable<number>;

  // Etuovi
  areaSearchMin: IDataLayerVariable<number | undefined>;
  areaSearchMax: IDataLayerVariable<number | undefined>;
  areaItem: IDataLayerVariable<number | undefined>;
  priceSearchMin: IDataLayerVariable<number | undefined>;
  priceSearchMax: IDataLayerVariable<number | undefined>;
  priceItem: IDataLayerVariable<number | undefined>;
  roomCount: IDataLayerVariable<string | undefined>;
  overallConditions: IDataLayerVariable<string | undefined>;
  newProperty: IDataLayerVariable<string | undefined>;
  officeId: IDataLayerVariable<number | string | undefined>;
  customerGroupId: IDataLayerVariable<number | undefined>;
  propertyType: IDataLayerVariable<string | null | undefined>;
  propertySubType: IDataLayerVariable<string | undefined>;
  propertySubTypes: IDataLayerVariable<string[] | undefined>;
  ownership: IDataLayerVariable<string[] | undefined>;
  heatingSystems: IDataLayerVariable<string[] | undefined>;
  constructionYearItem: IDataLayerVariable<number | undefined>;
  constructionYearSearchMin: IDataLayerVariable<number | undefined>;
  constructionYearSearchMax: IDataLayerVariable<number | undefined>;
  published: IDataLayerVariable<string | undefined>;
  locationTargeting: IDataLayerVariable<EoLocation>;
  shore: IDataLayerVariable<string | undefined>;
  loanCalculatorBank: IDataLayerVariable<string | undefined>;
}

type DataLayerImplementation = {
  name: string;
  id: string;
  data: ICustomVariables;
};

const dataLayer: DataLayerImplementation = {
  name: "Etuovi.com data layer",
  id: "etuovicom-dl",
  data: {
    viewPort: {
      name: "viewport",
      get: (): number => {
        return window.digitalData?.page?.attributes?.width ?? 0;
      },
    },
    sysEnv: {
      name: "system environment", // friendly name
      get: (): string => window?.digitalData?.page.pageInfo?.sysEnv ?? "",
    },
    destinationURL: {
      name: "destination url",
      get: (): string => {
        return window.digitalData?.page?.pageInfo?.locationHref ?? "";
      },
    },
    primaryCategory: {
      name: "primary category", // friendly name
      get: (): string =>
        window?.digitalData?.page?.category?.primaryCategory ?? "etusivu",
    },
    subCategory: {
      name: "sub category",
      get: (): string => window?.digitalData?.page?.category?.subCategory ?? "",
    },
    pageId: {
      // unique name
      name: "page ID", // friendly name
      get(): string {
        return window?.digitalData?.page?.pageInfo?.pageID ?? "";
      },
    },
    pageType: {
      name: "Page type",
      get: (): PageType => {
        const pageType = window?.digitalData?.page?.category?.pageType;
        if (pageType === "artikkelisivu") {
          return PageType.Article;
        }
        return window?.digitalData?.page?.category?.pageType ?? "";
      },
    },
    locationHref: {
      name: "location Href url", // friendly name
      get: (): string =>
        window?.digitalData?.page?.pageInfo?.locationHref ??
        window.location.href,
    },
    referrer: {
      name: "referring service", // friendly name
      get: (): string => window?.digitalData?.page?.pageInfo?.referrer,
    },
    section: {
      name: "section for ad load",
      get: (): string => {
        // You need to build up the logic to get the right section for the Ad Configuration
        // It doesn't matter where do you get the info, would be a part of an URL or combination of values located in digitalData.
        //
        // In case your site has responsive design you need to use the viewport or window.innerWidth or similiar to build the section names.
        //
        // In case you don't want to show ads on some part of your webiste, return noads.
        //
        // Set default section as a fallback, if for example digitalData is not available.
        const createSection = function (digitalData: any) {
          const allowedPages = ["itempage", "frontpage"];
          if (allowedPages.indexOf(digitalData.page.category.pageType) === -1) {
            return digitalData.page.category.primaryCategory;
          }

          const getUrlParameters = function (search: any) {
            const preparedQuery = [];
            if (typeof search !== "undefined" && search) {
              const prefixRemoved = search.substring(1);
              const splittedSearch = prefixRemoved.split("&");
              for (let i = 0; i < splittedSearch.length; i += 1) {
                const splittedParam = splittedSearch[i].split("=");
                preparedQuery.push({
                  key: splittedParam[0] && encodeURIComponent(splittedParam[0]),
                  value:
                    splittedParam[1] && encodeURIComponent(splittedParam[1]),
                });
              }
            }
            return preparedQuery;
          };

          const getQueryParamValue = function (paramName: any, query: any) {
            if (typeof query === "undefined" || !query) {
              return null;
            }
            for (let i = 0; i < query.length; i += 1) {
              const param = query[i];
              if (param.key === paramName) {
                return param.value;
              }
            }
            return null;
          };

          const createPrimaryCategory = (
            initialPrimaryCategory: any,
            referrer: any,
            utmSource: any,
            utmCampaing: any,
            bypass: any,
            adAlternative: any
          ) => {
            // ItemPage banner ad
            if (adAlternative) {
              return `${initialPrimaryCategory}_marketplace`;
            }

            const blockedSources = ["watchdog_content_immediate"];
            const blockedCampaigns = ["kohde"];
            if (
              (typeof bypass !== "undefined" && bypass) ||
              (typeof referrer !== "undefined" &&
                referrer &&
                referrer.match(/www.google.*\w+/g) !== null) ||
              (typeof utmSource !== "undefined" &&
                typeof utmCampaing !== "undefined" &&
                utmSource &&
                utmCampaing &&
                blockedSources.indexOf(utmSource) > -1) ||
              blockedCampaigns.indexOf(utmCampaing) > -1
            ) {
              // We are handling a source and campaign which should not show interstitial ads.
              return initialPrimaryCategory;
            }
            return `${initialPrimaryCategory}_interstitial`;
          };

          const referrer =
            digitalData &&
            digitalData.page &&
            digitalData.page.pageInfo &&
            digitalData.page.pageInfo.referrer
              ? digitalData.page.pageInfo.referrer
              : null;
          const query = getUrlParameters(document.location.search);
          const bypass = getQueryParamValue("intrst_bypass", query);
          const utmSource = getQueryParamValue("utm_source", query);
          const utmCampaign = getQueryParamValue("utm_campaign", query);
          const adAlternative =
            digitalData &&
            digitalData.product &&
            digitalData.product[0] &&
            digitalData.product[0].configurations &&
            digitalData.product[0].configurations.showBannerAd
              ? digitalData.product[0].configurations.showBannerAd
              : false;

          return createPrimaryCategory(
            digitalData.page.category.primaryCategory,
            referrer,
            utmSource,
            utmCampaign,
            bypass,
            adAlternative
          );
        };
        return window?.digitalData?.page?.category?.primaryCategory
          ? createSection(window.digitalData)
          : "noads";
      },
    },
    ads: {
      name: "enable / disable ads",
      get: (): boolean => {
        return true;
      },
    },
    pageTags: {
      name: "page tags", // friendly name
      get: (): string[] => {
        let tags: string[] = [];
        if (window?.digitalData?.page?.category?.tags) {
          const ddlTags = window.digitalData.page.category.tags as string[];
          tags = ddlTags.map((tag) => tag.toLowerCase());
        }
        return tags;
      },
    },
    validate: {
      // unique name
      name: "validate", // friendly name
      get: (): boolean => {
        return (
          window?.digitalData?.page?.category?.primaryCategory ===
          "artikkelisivu"
        );
      },
    },
    subCategory1: {
      name: "sub category 1", // friendly name
      get: (): string =>
        window?.digitalData?.page?.category?.subCategory1 ?? "",
    },
    accountType: {
      name: "user account type",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.accountType;
      },
    },
    alma_id: {
      name: "alma_id hash",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.almaIDHash;
      },
    },
    amid: {
      name: "alma master id",
      get: (): string | undefined => {
        try {
          return window.localStorage.getItem("gravitoOPUid") ?? undefined;
        } catch (e) {
          return undefined;
        }
      },
    },
    cdime: {
      name: "sha256 hash of email",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.cdime ?? window?.ALMA?.tunnus?.getUser()?.cdime;
      },
    },
    cdimu: {
      name: "sha256 hash of email",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.cdimu;
      },
    },
    cid: {
      name: "content id",
      get: (): string | undefined => {
        return window?.digitalData?.page?.attributes?.content?.cid;
      },
    },
    areaSearchMin: {
      name: "search min area",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        if (digitalData && !digitalData.product) {
          // Only if not @ ItemPage
          if (digitalData.page.attributes.searchParameters.sizeMin) {
            return digitalData.page.attributes.searchParameters.sizeMin;
          }
        }
        return undefined;
      },
    },
    areaSearchMax: {
      name: "search max area",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        if (digitalData && !digitalData.product) {
          // Only if not @ ItemPage
          if (
            digitalData.page &&
            digitalData.page.attributes &&
            digitalData.page.attributes.searchParameters &&
            digitalData.page.attributes.searchParameters.sizeMax
          ) {
            return digitalData.page.attributes.searchParameters.sizeMax;
          }
        }
        return undefined;
      },
    },
    areaItem: {
      name: "Living area",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          digitalData.product &&
          digitalData.product.length > 0
        ) {
          return digitalData.product[0].productInfo.apartmentfeatures
            .livingArea;
        }
        return undefined;
      },
    },
    priceSearchMin: {
      name: "search min price",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          (!digitalData.product || digitalData.product.length < 1)
        ) {
          // Only if not @ ItemPage
          if (
            digitalData.page &&
            digitalData.page.attributes &&
            digitalData.page.attributes.searchParameters
          ) {
            return (
              digitalData.page.attributes.searchParameters.priceMin || null
            );
          }
        }
        return undefined;
      },
    },
    priceSearchMax: {
      name: "search max price",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          (!digitalData.product || digitalData.product.length < 1)
        ) {
          // Only if not @ ItemPage
          if (
            digitalData.page &&
            digitalData.page.attributes &&
            digitalData.page.attributes.searchParameters
          ) {
            return (
              digitalData.page.attributes.searchParameters.priceMax || null
            );
          }
        }
        return undefined;
      },
    },
    priceItem: {
      name: "item price",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          digitalData.product &&
          digitalData.product.length > 0
        ) {
          return digitalData.product[0].productInfo.basePrice;
        }
        return undefined;
      },
    },
    roomCount: {
      name: "search or itempage roomCount(s)",
      get: (): string | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          digitalData.product &&
          digitalData.product.length > 0
        ) {
          // ITEMPAGE
          return digitalData.product[0].productInfo.apartmentfeatures.roomCount;
        }
        if (
          digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.searchParameters &&
          digitalData.page.attributes.searchParameters.roomCounts
        ) {
          return digitalData.page.attributes.searchParameters.roomCounts;
        }
        return undefined;
      },
    },
    overallConditions: {
      name: "search or itempage overallCondition(s)",
      get: (): string | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          digitalData.product &&
          digitalData.product.length > 0
        ) {
          // ITEMPAGE
          return digitalData.product[0].productInfo.apartmentfeatures.condition;
        }
        if (
          digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.searchParameters &&
          digitalData.page.attributes.searchParameters.overallConditions
        ) {
          return digitalData.page.attributes.searchParameters.overallConditions;
        }
        return undefined;
      },
    },
    newProperty: {
      name: "is newProperty or not",
      get: (): string | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          digitalData.product &&
          digitalData.product.length > 0
        ) {
          // ITEMPAGE
          return digitalData.product[0].productInfo.apartmentfeatures
            .newProperty;
        }
        if (
          digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.searchParameters &&
          digitalData.page.attributes.searchParameters
            .newBuildingSearchCriteria !== null
        ) {
          // OTHERPAGE
          // TODO: Confirm purpose, bool to string?
          return (
            digitalData.page.attributes.searchParameters
              .newBuildingSearchCriteria === "ONLY_NEW_BUILDINGS"
          )
            .valueOf()
            .toString();
        }
        return undefined;
      },
    },
    officeId: {
      name: "officeId",
      get: (): number | string | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          digitalData.product &&
          digitalData.product.length > 0
        ) {
          // ITEMPAGE
          return digitalData.product[0].productInfo.realestateagency.officeId;
        }
        // OTHER PAGES
        return "NOT_AVAILABLE";
      },
    },
    customerGroupId: {
      name: "customerGroupId",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        return (
          digitalData &&
          digitalData.product &&
          digitalData.product[0] &&
          digitalData.product[0].productInfo &&
          digitalData.product[0].productInfo.realestateagency &&
          digitalData.product[0].productInfo.realestateagency.customerGroupId
        );
      },
    },
    propertyType: {
      name: "propertyType",
      get: (): string | null | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          digitalData.product &&
          digitalData.product.length > 0
        ) {
          // ITEMPAGE
          return digitalData.product[0].category.primaryCategory;
        }
        if (
          digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.searchParameters &&
          digitalData.page.attributes.searchParameters.propertyType
        ) {
          // OTHER PAGES
          return digitalData.page.attributes.searchParameters.propertyType;
        }
        return null;
      },
    },
    propertySubType: {
      name: "propertySubType",
      get: (): string | undefined => {
        const digitalData = window?.digitalData;
        return digitalData &&
          digitalData.product &&
          digitalData.product[0] &&
          digitalData.product[0].productInfo &&
          digitalData.product[0].productInfo.apartmentfeatures &&
          digitalData.product[0].productInfo.apartmentfeatures.propertySubType
          ? digitalData.product[0].productInfo.apartmentfeatures.propertySubType
          : "";
      },
    },
    propertySubTypes: {
      name: "propertySubTypes",
      get: (): string[] | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.searchParameters
        ) {
          const searchParams = digitalData.page.attributes.searchParameters;
          if (searchParams.residentialPropertyTypes) {
            return searchParams.residentialPropertyTypes;
          }
          if (searchParams.leisurePropertyTypes) {
            return searchParams.leisurePropertyTypes;
          }
          if (searchParams.plotPropertyTypes) {
            return searchParams.plotPropertyTypes;
          }
          if (searchParams.estatePropertyTypes) {
            return searchParams.estatePropertyTypes;
          }
          if (searchParams.otherPropertyTypes) {
            return searchParams.otherPropertyTypes;
          }
        }
        return [];
      },
    },
    ownership: {
      name: "ownership",
      get: (): string[] | undefined => {
        const digitalData = window?.digitalData;
        const ownershipTypeMapper = (ownershipType?: string) => {
          switch (ownershipType) {
            case "OWN":
              return "own";
            case "PARTIAL_OWNERSHIP":
              return "partial";
            case "LIVING_RIGHT":
              return "oright";
            default:
              return undefined;
          }
        };
        // ITEM PAGE
        if (digitalData && digitalData.product && digitalData.product[0]) {
          const val = ownershipTypeMapper(
            digitalData.product[0].productInfo.apartmentfeatures.ownershipType
          );
          if (val) {
            return [val];
          }
        }
        // LIST PAGE
        return (
          digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.searchParameters &&
          digitalData.page.attributes.searchParameters.ownershipTypes &&
          digitalData.page.attributes.searchParameters.ownershipTypes.map(
            ownershipTypeMapper
          )
        );
      },
    },
    heatingSystems: {
      name: "heatingSystems",
      get: (): string[] | undefined => {
        const digitalData = window?.digitalData;

        const heatingSystemMapper = (heatingSystem: string) => {
          switch (heatingSystem) {
            case "DISTRICT_HEATING":
              return "DH";
            case "OIL":
              return "O";
            case "ELECTRIC":
              return "E";
            case "WOOD":
              return "W";
            case "GEOTHERMAL_HEATING":
              return "GH";
            case "HEAT_PUMP":
              return "HP";
            default:
              return undefined;
          }
        };

        // ITEM PAGE
        if (digitalData && digitalData.product && digitalData.product[0]) {
          return (
            digitalData.product[0].productInfo.apartmentfeatures
              .heatingSystems &&
            digitalData.product[0].productInfo.apartmentfeatures.heatingSystems.map(
              heatingSystemMapper
            )
          );
        }
        // LIST PAGE
        return (
          digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.searchParameters &&
          digitalData.page.attributes.searchParameters.heatingSystems &&
          digitalData.page.attributes.searchParameters.heatingSystems.map(
            heatingSystemMapper
          )
        );
      },
    },
    constructionYearItem: {
      name: "constructionYearItem",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        // ITEM PAGE
        if (digitalData && digitalData.product && digitalData.product[0]) {
          return digitalData.product[0].productInfo.apartmentfeatures
            .constructionYear;
        }
        return undefined;
      },
    },
    constructionYearSearchMin: {
      name: "constructionYearSearchMin",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          (!digitalData.product || digitalData.product.length < 1)
        ) {
          // Only if not @ ItemPage
          if (
            digitalData.page &&
            digitalData.page.attributes &&
            digitalData.page.attributes.searchParameters
          ) {
            return digitalData.page.attributes.searchParameters.yearMin || null;
          }
        }
        return undefined;
      },
    },
    constructionYearSearchMax: {
      name: "constructionYearSearchMax",
      get: (): number | undefined => {
        const digitalData = window?.digitalData;
        if (
          digitalData &&
          (!digitalData.product || digitalData.product.length < 1)
        ) {
          // Only if not @ ItemPage
          if (
            digitalData.page &&
            digitalData.page.attributes &&
            digitalData.page.attributes.searchParameters
          ) {
            return digitalData.page.attributes.searchParameters.yearMax || null;
          }
        }
        return undefined;
      },
    },
    published: {
      name: "published",
      get: (): string | undefined => {
        const digitalData = window?.digitalData;
        const publishingTimeMapper = (publishingTime: string) => {
          switch (publishingTime) {
            case "WITHIN_ONE_DAY":
              return "24h";
            case "WITHIN_TWO_DAYS":
              return "48h";
            case "WITHIN_SEVEN_DAYS":
              return "7d";
            case "WITHIN_TWO_WEEKS":
              return "2w";
            case "ANY_DAY":
              return "anyd";
            default:
              return undefined;
          }
        };

        // ITEM PAGE
        if (digitalData && digitalData.product && digitalData.product[0]) {
          return publishingTimeMapper(
            digitalData.product[0].productInfo.published
          );
        }
        // LIST PAGE
        return (
          digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.searchParameters &&
          publishingTimeMapper(
            digitalData.page.attributes.searchParameters
              .publishingTimeSearchCriteria
          )
        );
      },
    },
    locationTargeting: {
      name: "locationTargeting",
      get: (): EoLocation => {
        const locationTargeting: EoLocation = {
          countryCode: "FI",
          countryName: null,
          regionCode: null,
          regionName: null,
          municipalityCode: null,
          municipalityName: null,
          districtCode: null,
          districtName: null,
          nickname: null,
          postCodeAndOffice: null,
          countries: [],
          regions: [],
          municipalities: [],
          districts: [],
          nicknames: [],
          municipalityFromUrl: null,
          districtFromUrl: null,
        };

        const pushToArray = (arr: string[], value: string) => {
          if (value && value !== "" && arr.indexOf(value) === -1)
            arr.push(value);
        };

        const digitalData = window?.digitalData;

        if (
          digitalData &&
          digitalData.product &&
          digitalData.product.length > 0
        ) {
          // ITEMPAGE
          const locationObj = digitalData.product[0].productInfo.location;
          locationTargeting.countryCode = locationObj.address.countryCode;
          locationTargeting.countryName = locationObj.address.countryName;
          locationTargeting.regionCode = locationObj.address.regionCode;
          locationTargeting.regionName = locationObj.address.regionName;
          locationTargeting.municipalityName = locationObj.address.municipality;
          locationTargeting.districtName = locationObj.address.district;
          locationTargeting.postCodeAndOffice = locationObj.address.postalCode;

          // Nickname defaultNames to array
          for (
            let ind = 0;
            ind < locationObj.address.areaNicknames.length;
            ind += 1
          ) {
            const nn = locationObj.address.areaNicknames[ind];
            pushToArray(locationTargeting.nicknames, nn.defaultName);
          }
        } else if (
          digitalData &&
          digitalData.page.attributes.contentLocations &&
          digitalData.page.attributes.contentLocations.length > 0
        ) {
          locationTargeting.municipalityFromUrl =
            digitalData.page.attributes.contentLocations[0].city;
          locationTargeting.districtFromUrl =
            digitalData.page.attributes.contentLocations[0].district;
        } else if (
          digitalData &&
          digitalData.page.attributes.searchParameters &&
          digitalData.page.attributes.searchParameters.locationSearchCriteria &&
          digitalData.page.attributes.searchParameters.locationSearchCriteria
            .classifiedLocationTerms &&
          digitalData.page.attributes.searchParameters.locationSearchCriteria
            .classifiedLocationTerms.length > 0
        ) {
          // OTHERPAGES THAN ITEMPAGE
          const locations =
            digitalData.page.attributes.searchParameters.locationSearchCriteria
              .classifiedLocationTerms;
          const locationObj = locations[0];
          locationTargeting.countryCode = locationObj.parentCountryCode;
          locationTargeting.regionName = locationObj.parentRegionName;
          locationTargeting.regionCode = locationObj.parentRegionCode;
          locationTargeting.municipalityName =
            locationObj.parentMunicipalityName;
          locationTargeting.municipalityCode =
            locationObj.parentMunicipalityCode;
          locationTargeting.districtName = locationObj.parentDistrictName;
          locationTargeting.districtCode = locationObj.parentDistrictCode;

          if (locationObj.type === "COUNTRY") {
            locationTargeting.countryCode = locationObj.code;
            locationTargeting.countryName = locationObj.fullName;
          } else if (locationObj.type === "REGION") {
            locationTargeting.regionCode = locationObj.code;
          } else if (locationObj.type === "POST_CODE") {
            locationTargeting.postCodeAndOffice = locationObj.fullName;
          } else if (locationObj.type.endsWith("_NN")) {
            // Ensimmäisen lokaatiolta otettavat parent arvot tulevat jo aiemmista jos on tullakseen.
          }

          for (let ind = 0; ind < locations.length; ind += 1) {
            const loc = locations[ind];
            if (loc.type === "COUNTRY") {
              pushToArray(locationTargeting.countries, loc.code);
            } else if (loc.type === "REGION") {
              pushToArray(locationTargeting.countries, loc.parentCountryCode);
              pushToArray(locationTargeting.regions, loc.fullName);
            } else if (loc.type === "CITY") {
              pushToArray(locationTargeting.countries, loc.parentCountryCode);
              pushToArray(locationTargeting.regions, loc.parentRegionName);
              pushToArray(locationTargeting.municipalities, loc.fullName);
            } else if (loc.type === "DISTRICT") {
              pushToArray(locationTargeting.countries, loc.parentCountryCode);
              pushToArray(locationTargeting.regions, loc.parentRegionName);
              pushToArray(
                locationTargeting.municipalities,
                loc.parentMunicipalityName
              );
              pushToArray(locationTargeting.districts, loc.shortName);
            } else if (loc.type.endsWith("_NN")) {
              pushToArray(locationTargeting.countries, loc.parentCountryCode);
              pushToArray(locationTargeting.regions, loc.parentRegionName);
              pushToArray(
                locationTargeting.municipalities,
                loc.parentMunicipalityName
              );
              pushToArray(locationTargeting.districts, loc.parentDistrictName);
              pushToArray(locationTargeting.nicknames, loc.fullName);
            } else {
              pushToArray(locationTargeting.countries, loc.parentCountryCode);
              pushToArray(locationTargeting.regions, loc.parentRegionName);
              pushToArray(
                locationTargeting.municipalities,
                loc.parentMunicipalityName
              );
              pushToArray(locationTargeting.districts, loc.parentDistrictName);
            }
          }
        }

        // console.log('ENSIGHTEN aa locationTargeting');
        // console.log(locationTargeting);
        // TODO: What is the shape of this in the end?
        return locationTargeting;
      },
    },
    shore: {
      name: "shore",
      get: (): string | undefined => {
        const digitalData = window?.digitalData;
        try {
          return digitalData.product[0].productInfo.apartmentfeatures.shore
            .shoreRightType;
        } catch (e) {
          return undefined;
        }
      },
    },
    loanCalculatorBank: {
      // Which bank's loan calculator page
      name: "loan calculator", // friendly name
      get: (): string | undefined => {
        const digitalData = window?.digitalData;
        return digitalData &&
          digitalData.page &&
          digitalData.page.attributes &&
          digitalData.page.attributes.loanCalculatorBank
          ? digitalData.page.attributes.loanCalculatorBank
          : "";
      },
    },
  },
};

export default dataLayer;
export type { ICustomVariables };
